// SettingsHeader.jsx
import React from "react";
import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SettingsHeader = ({
  title = "Settings",
  description = "",
  buttons = [],
  showBack = false,
  onBack,
  backTo = -1,
  icon: TitleIcon,
  children,
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) onBack();
    else navigate(backTo);
  };

  const variantStyles = {
    default: {
      className: "text-white shadow-sm transition-all duration-200",
      style: { 
        backgroundColor: 'var(--primary-color, #4f46e5)'
      },
      hoverStyle: {
        filter: 'brightness(0.9)'
      }
    },
    primary: {
      className: "text-white shadow-sm transition-all duration-200",
      style: { 
        backgroundColor: 'var(--primary-color, #4f46e5)'
      },
      hoverStyle: {
        filter: 'brightness(0.9)'
      }
    },
    secondary:
      "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 focus:ring-gray-500",
    destructive: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500",
    outline:
      "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-all duration-200 focus:ring-gray-500",
  };

  const baseButtonClasses = `
    inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
    transition-all focus:outline-none focus:ring-2 focus:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  return (
    <header className="sticky top-0 z-30 w-full mb-4">
      <div className="bg-white border-b border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:flex-nowrap">
            {/* Left side: Back button + Title & Description */}
            <div className="flex items-center gap-3">
              {showBack && (
                <button
                  onClick={handleBack}
                  className="flex items-center justify-center w-8 h-8 text-gray-600 bg-gray-50 border border-gray-100 transition-all rounded-lg hover:bg-gray-100 shadow-sm"
                  onMouseOver={(e) => e.currentTarget.style.color = 'var(--primary-color, #4f46e5)'}
                  onMouseOut={(e) => e.currentTarget.style.color = ''}
                  aria-label="Go back"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}

              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {TitleIcon && (
                    <div className="p-1 rounded-md" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)' }}>
                      <TitleIcon className="w-4 h-4" style={{ color: 'var(--primary-color, #4f46e5)' }} />
                    </div>
                  )}
                  <h1 className="text-base font-bold text-gray-900">
                    {title}
                  </h1>
                </div>
                {description && (
                  <p className="text-[10px] text-gray-500 max-w-2xl leading-tight">
                    {description}
                  </p>
                )}
              </div>
            </div>

            {/* Right side: Actions & Filters */}
            <div className="flex flex-1 items-center justify-end gap-4 min-w-0">
              {/* Children (usually filters/search) */}
              {children && (
                <div className="flex-1 flex items-center justify-end gap-2 min-w-0 max-w-full lg:max-w-none">
                  {children}
                </div>
              )}
              
              {/* Buttons */}
              <div className="flex items-center gap-2 shrink-0">
                {buttons.map((button, index) => {
                  const {
                    label,
                    onClick,
                    variant = "default",
                    icon: Icon,
                    loading = false,
                    disabled = false,
                  } = button;

                  return (
                    <button
                      key={index}
                      onClick={onClick}
                      disabled={disabled || loading}
                      className={`${baseButtonClasses} ${
                        variantStyles[variant] 
                          ? (typeof variantStyles[variant] === 'string' ? variantStyles[variant] : variantStyles[variant].className)
                          : (typeof variantStyles.default === 'string' ? variantStyles.default : variantStyles.default.className)
                      } whitespace-nowrap`}
                      style={typeof variantStyles[variant] === 'object' ? variantStyles[variant].style : {}}
                      onMouseOver={(e) => {
                        if (variant === 'default') {
                          e.currentTarget.style.filter = 'brightness(0.9)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (variant === 'default') {
                          e.currentTarget.style.filter = '';
                        }
                      }}
                    >
                      {loading ? (
                        <div className="w-3 h-3 border-2 border-current rounded-full border-t-transparent animate-spin" />
                      ) : (
                        <>
                          {Icon && <Icon className="w-3.5 h-3.5" />}
                          <span className="hidden sm:inline">{label}</span>
                          <span className="sm:hidden">{Icon ? "" : label}</span>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default SettingsHeader;
